import { useEffect, useState } from 'react';
import type { Tenant } from '@etiquetachef/types';
import {
  getTenantSubscriptionOnce,
  type BillingDocResult,
} from '@/lib/firestoreTenantDetail';

/**
 * Carrega `billing/subscription` de cada tenant da página atual da lista de empresas, para a
 * coluna "Assinatura" derivar o status comercial.
 *
 * Origem: #100 — 2026-06-28 13:52 +0200 (badge "Ativo" verde para tenant que nunca pagou:
 * a grade mostrava só `tenant.status` cru e escondia quem nunca pagou).
 *
 * Por quê: a lista vem da callable `searchTenants` (snapshot, não tempo-real), então são gets
 * one-shot em paralelo — não listeners — refeitos quando o conjunto de IDs muda (página,
 * refresh, filtro). Custo: 1 read por tenant exibido (≤ pageSize, hoje no máx. 50), aceitável
 * num painel de super-admin; se virar gargalo, desnormalizar o status no doc do tenant.
 *
 * Contrato: devolve `Map id→BillingDocResult`; entrada AUSENTE = ainda carregando (o caller
 * trata como pendente). Na troca de página o Map é zerado antes de recarregar. `idsKey` é a
 * dependência estável do effect — o array `tenants` é recriado pelo pai a cada render.
 *
 * Usado por: `TenantList` (`apps/admin`).
 *
 * Arquitetura: fonte da verdade do plano é `tenants/{tid}/billing/subscription` (CLAUDE.md,
 * "Plano efetivo do tenant"); leitura via `getTenantSubscriptionOnce`
 * (`@/lib/firestoreTenantDetail`), sob a rule super-admin de `billing/**`.
 */
export function useTenantBillingStatuses(tenants: Tenant[]): Map<string, BillingDocResult> {
  const [statuses, setStatuses] = useState<Map<string, BillingDocResult>>(new Map());
  // Chave estável: só re-dispara quando o conjunto de IDs muda, não a cada
  // render (o array `tenants` é recriado pelo pai a cada render).
  const idsKey = tenants.map((t) => t.id).join(',');

  useEffect(() => {
    if (tenants.length === 0) {
      setStatuses(new Map());
      return;
    }
    let cancelled = false;
    const ids = tenants.map((t) => t.id);
    // Reset para "todos pendentes" (Map vazio) na troca de página.
    setStatuses(new Map());
    void Promise.all(
      ids.map(async (id): Promise<readonly [string, BillingDocResult]> => [
        id,
        await getTenantSubscriptionOnce(id),
      ]),
    ).then((entries) => {
      if (!cancelled) setStatuses(new Map(entries));
    });
    return (): void => {
      cancelled = true;
    };
    // `tenants` é intencionalmente omitido — `idsKey` é a dependência estável.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return statuses;
}
